import { create, StateCreator } from 'zustand';
import { login, register, forgotPassword, resetPassword } from '../services/auth.service';
import { refreshWithQueue } from '../services/axios';
import { getOrganization } from '../services/organization.service';
import { LoginRequest, RegisterRequest, User, ForgotPasswordRequest, ResetPasswordRequest, Role } from '../types/auth.type';
import { decodeJWT } from '../utils/jwt';
import { mapJwtOrganizationAccess } from '../utils/roles.utils';
import { forgotPasswordRateLimiter } from '../utils/rateLimiter';
import { Organization } from '../types/organization.type';
import OneSignalService from '../services/oneSignal.service';
import { identifyUser, resetUser } from '../analytics/mixpanel';
import { disableDemoMode, isDemoMode, buildFakeJwt } from '../demo/demoMode';
import { buildDemoUser } from '../demo/demoData';

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isLoading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  isInitialized: boolean;
  /** True while dashboard shell loads org/nav data after login or session restore. */
  isAppBootstrapping: boolean;
  startAppBootstrap: () => void;
  finishAppBootstrap: () => void;
  login: (data: LoginRequest) => Promise<void>;
  register: (data: RegisterRequest) => Promise<void>;
  logout: () => Promise<void>;
  initializeAuth: () => void;
  forgotPassword: (data: ForgotPasswordRequest) => Promise<void>;
  resetPassword: (data: ResetPasswordRequest) => Promise<void>;
  fetchUserOrganization: (organizationId: string) => Promise<void>;
  refreshUserData: () => Promise<void>;
}

export const useAuthStore = create<AuthState>(((set, get) => ({
  user: null,
  accessToken: null,
  refreshToken: null,
  isLoading: false,
  error: null,
  isAuthenticated: false,
  isInitialized: false,
  isAppBootstrapping: false,
  startAppBootstrap: () => set({ isAppBootstrapping: true }),
  finishAppBootstrap: () => set({ isAppBootstrapping: false }),
  login: async (data: LoginRequest) => {
    // Ensure a real login never runs against the fake demo data layer.
    disableDemoMode();
    set({ isLoading: true, isAppBootstrapping: false, error: null });
    try {
      const response = await login(data);
      const { access_token, refresh_token } = response;
      localStorage.setItem('accessToken', access_token);
      localStorage.setItem('refreshToken', refresh_token);
      
      let decoded;
      try {
        decoded = decodeJWT(access_token) as import('../utils/jwt').JWTPayload;
      } catch (jwtError) {
        console.error('JWT decoding failed:', jwtError);
        throw new Error('Invalid authentication token received');
      }
      
      const user: User = {
        id: decoded.sub, // Use 'sub' as the user ID
        email: decoded.username, // Use 'username' as the email
        name: decoded.name || 'User', // Use name from JWT or fallback to 'User' instead of email
        data: {
          role: (decoded.role ?? Role.ADMIN) as Role,
          access: mapJwtOrganizationAccess(
            decoded.organizationIds,
            decoded.permissions,
            decoded.customPermissions,
          ),
          phone: decoded.phone || undefined,
          managedAccountantIds: decoded.managedAccountantIds,
        },
        organization: undefined,
      };

      localStorage.setItem('user', JSON.stringify(user));
      set({ accessToken: access_token, refreshToken: refresh_token, isAuthenticated: true, isLoading: false, user, isInitialized: true });

      const isClientRole = user.data.role === Role.ORGANIZATION
        || user.data.role === Role.ORGANIZATION_USER
        || user.data.role === Role.AUDITOR;
      if (isClientRole && user.data.access.length > 0) {
        const firstOrgId = user.data.access[0].organization_id;
        try {
          const organization = await getOrganization(firstOrgId);
          const userWithOrg = { ...user, organization };
          set({ user: userWithOrg });
          localStorage.setItem('user', JSON.stringify(userWithOrg));
        } catch (error) {
          console.error('Failed to fetch organization data on login:', error);
        }
      }

      // Link this session to the authenticated user in Mixpanel.
      const identifiedUser = get().user;
      if (identifiedUser) identifyUser(identifiedUser);
    } catch (error: any) {
      let errorMessage = 'Login failed';
      
      // Handle different types of errors
      if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const data = error.response.data;
        
        // Check for both 'detail' (FastAPI standard) and 'message' fields
        const serverMessage = data?.detail || data?.message;
        
        if (status === 400) {
          errorMessage = serverMessage || 'Invalid email or password';
        } else if (status === 401) {
          errorMessage = serverMessage || 'Unauthorized access';
        } else if (status === 403) {
          errorMessage = serverMessage || 'Access forbidden';
        } else if (status === 404) {
          errorMessage = serverMessage || 'User not found';
        } else if (status === 500) {
          errorMessage = serverMessage || 'Server error. Please try again later';
        } else {
          errorMessage = serverMessage || `Login failed (${status})`;
        }
      } else if (error.request) {
        // Network error - check if it's a connection refused error
        if (error.code === 'ERR_NETWORK' || error.message.includes('Network Error')) {
          errorMessage = 'Unable to connect to the server. Please check if the server is running and try again.';
        } else {
          errorMessage = 'Unable to connect to the server. Please check your internet connection and try again.';
        }
      } else {
        // Other errors
        errorMessage = error.message || 'Login failed';
      }
      
      set({ error: errorMessage, isLoading: false, isAuthenticated: false, isAppBootstrapping: false });
      throw error; // Re-throw to allow component to handle
    }
  },

  register: async (data: RegisterRequest) => {
    set({ isLoading: true, error: null });
    try {
      await register(data);
      set({ isLoading: false });
    } catch (error: any) {
      const errorMessage = error.message || 'Registration failed';
      set({ error: errorMessage, isLoading: false });
      throw error; // Re-throw to allow component to handle
    }
  },

  logout: async () => {
    // Exit demo mode (if active) so a later real session is never faked.
    disableDemoMode();
    // Unregister from OneSignal backend BEFORE clearing tokens
    try {
      const success = await OneSignalService.unregisterPlayerFromBackend();
      if (success) {
        console.log('OneSignal: Successfully unregistered from backend');
      } else {
        console.warn('OneSignal: Failed to unregister from backend');
      }
    } catch (error) {
      console.error('OneSignal: Error unregistering from backend:', error);
    }

    // Now clear tokens and logout
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('selectedOrg'); // Clear selected organization on logout
    resetUser();
    set({ user: null, accessToken: null, refreshToken: null, isAuthenticated: false, isInitialized: false, isAppBootstrapping: false });
  },

  initializeAuth: () => {
    // In demo mode always restore the canonical fake client session so the chat
    // never falls back to a stale name or the organization name.
    if (isDemoMode()) {
      const token = localStorage.getItem('accessToken') || buildFakeJwt();
      set({
        user: buildDemoUser(),
        accessToken: token,
        refreshToken: token,
        isAuthenticated: true,
        isInitialized: true,
        isAppBootstrapping: true,
      });
      return;
    }

    const accessToken = localStorage.getItem('accessToken');
    const refreshToken = localStorage.getItem('refreshToken');
    const userStr = localStorage.getItem('user');
    let user: User | null = null;
    if (userStr) {
      try {
        user = JSON.parse(userStr);
      } catch {
        user = null;
      }
    }
    if (accessToken && refreshToken && user) {
      // Restore session silently — boot overlay is shown only when DashboardLayout mounts.
      try {
        decodeJWT(accessToken);
      } catch {
        // Corrupt access token with no way to recover — clear stale session.
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        localStorage.removeItem('expirationTime');
        set({ isAuthenticated: false, user: null, isInitialized: true, isAppBootstrapping: false });
        return;
      }
      set({ accessToken, refreshToken, isAuthenticated: true, user, isInitialized: true, isAppBootstrapping: false });
      identifyUser(user);
    } else if (accessToken || refreshToken || userStr) {
      // Partial/corrupt session data — clear so auth pages don't get stuck.
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      localStorage.removeItem('expirationTime');
      set({ isAuthenticated: false, user: null, isInitialized: true, isAppBootstrapping: false });
    } else {
      set({ isAuthenticated: false, user: null, isInitialized: true, isAppBootstrapping: false });
    }
  },

  forgotPassword: async (data: ForgotPasswordRequest) => {
    // Check rate limiting before proceeding
    const rateLimitCheck = forgotPasswordRateLimiter.canAttempt();
    
    if (!rateLimitCheck.allowed) {
      const remainingTime = rateLimitCheck.remainingTime;
      const formattedTime = forgotPasswordRateLimiter.formatTime(remainingTime!);
      throw new Error(`Too many attempts. Please try again in ${formattedTime}.`);
    }

    set({ isLoading: true, error: null });
    try {
      await forgotPassword(data);
      // Reset rate limiter on successful request
      forgotPasswordRateLimiter.reset();
      set({ isLoading: false });
    } catch (error: any) {
      // Record failed attempt
      forgotPasswordRateLimiter.recordAttempt();
      set({ error: error.message || 'Forgot password failed', isLoading: false });
      throw error; // Re-throw to allow component to handle
    }
  },

  resetPassword: async (data: ResetPasswordRequest) => {
    set({ isLoading: true, error: null });
    try {
      await resetPassword(data);
      set({ isLoading: false });
    } catch (error: any) {
      set({ error: error.message || 'Reset password failed', isLoading: false });
      throw error; // Re-throw to allow component to handle
    }
  },

  fetchUserOrganization: async (organizationId: string) => {
    try {
      const organization = await getOrganization(organizationId);
      const currentState = get();
      set({
        user: currentState.user ? { ...currentState.user, organization } : null
      });
    } catch (error: any) {
      console.error('Failed to fetch organization:', error);
      // Don't throw error here to avoid breaking the app flow
    }
  },

  refreshUserData: async () => {
    try {
      const currentState = get();
      
      // Request new token with updated permissions.
      // Goes through the shared single-flight refresh in axios.ts so this
      // never races against a refresh triggered independently by another
      // in-flight request's interceptor (which would rotate the refresh
      // token out from under this call and cause a 400).
      if (currentState.refreshToken) {
        const access_token = await refreshWithQueue();
        const refresh_token = localStorage.getItem('refreshToken') || currentState.refreshToken;
        
        // Decode new token
        const decoded = decodeJWT(access_token) as import('../utils/jwt').JWTPayload;
        
        if (currentState.user) {
          const updatedUser: User = {
            ...currentState.user,
            data: {
              role: (decoded.role ?? Role.ADMIN) as Role,
              access: mapJwtOrganizationAccess(
                decoded.organizationIds,
                decoded.permissions,
                decoded.customPermissions,
              ),
              phone: decoded.phone || currentState.user.data.phone,
              language: currentState.user.data.language,
              managedAccountantIds: decoded.managedAccountantIds,
              sector_manager_id: currentState.user.data.sector_manager_id,
            },
          };
          
          set({ 
            accessToken: access_token, 
            refreshToken: refresh_token,
            user: updatedUser 
          });
          localStorage.setItem('user', JSON.stringify(updatedUser));

          if (
            (updatedUser.data.role === Role.ORGANIZATION
              || updatedUser.data.role === Role.ORGANIZATION_USER
              || updatedUser.data.role === Role.AUDITOR)
            && updatedUser.data.access.length > 0
          ) {
            const firstOrgId = updatedUser.data.access[0].organization_id;
            try {
              const organization = await getOrganization(firstOrgId);
              const userWithOrg = { ...updatedUser, organization };
              set({ user: userWithOrg });
              localStorage.setItem('user', JSON.stringify(userWithOrg));
            } catch (error) {
              console.error('Failed to fetch organization data on refresh:', error);
            }
          }
        }
      }
    } catch (error: any) {
      console.error('Failed to refresh user data:', error);
      // Silently fail - user can re-login if needed
    }
  },
})) as StateCreator<AuthState>); 